import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useShopNotifications } from "../context/ShopNotificationsContext";
import "../styles/shopNotifications.css";

/* =========================
   Helpers
   ========================= */
function timeAgo(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function getType(n) {
  return String(n?.type || "").toLowerCase().trim();
}

function isOrderType(t) {
  return (
    t === "order_placed" ||
    t === "order_cancelled" ||
    t === "order_dispute" ||
    t === "order_status_updated" ||
    t === "order_shipped" ||
    t === "order_delivered" ||
    t === "order_completed"
  );
}
function isReturnType(t) {
  return t === "order_returned" || t === "return_status_updated";
}
function isMessageType(t) {
  return t.startsWith("message_");
}
function isStockType(t) {
  return t === "low_stock" || t === "out_of_stock";
}
function isSocialType(t) {
  return (
    t === "like_post" ||
    t === "comment_post" ||
    t === "reply_comment" ||
    t === "new_follower" ||
    t === "shop_follow" ||
    t === "mention_post" ||
    t === "story_reaction" ||
    t === "story_comment" ||
    t === "shop_review"
  );
}

function typeIcon(t) {
  if (isOrderType(t)) return "📦";
  if (isReturnType(t)) return "↩️";
  if (isMessageType(t) || t === "story_reply") return "✉️";
  if (t === "story_reaction") return "❤️";
  if (t === "story_comment") return "💬";
  if (isStockType(t)) return "⚠️";
  if (isSocialType(t)) return "💬";
  if (t === "new_follower" || t === "shop_follow" || t === "shop_review") return "⭐";
  if (t === "commission") return "💰";
  return "🔔";
}

function getTargetUrl(n) {
  const t = getType(n);
  const source = String(n?.meta?.source || "").toLowerCase().trim();

  if (isReturnType(t)) return "/shop/returns";
  if (isOrderType(t)) return "/shop/orders";
  if (isStockType(t)) return "/shop/products";
  if (source === "shop_feed") return "/shop/feed";
  if (isMessageType(t) || t === "story_reply") {
    const cid = String(n?.meta?.conversationId || "");
    return cid ? `/shop/messages?c=${encodeURIComponent(cid)}` : "/shop/messages";
  }
  if (t === "new_follower" || t === "shop_follow") return "/shop/customers"; // followers tab
  if (t === "shop_review") return "/shop/reviews";
  if (t === "commission") return "/shop/earnings";
  return "/shop/notifications";
}

/* =========================
   Tabs
   ========================= */
const TABS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "orders", label: "Orders" },
  { key: "returns", label: "Returns" },
  { key: "messages", label: "Messages" },
  { key: "stock", label: "Stock" },
  { key: "social", label: "Social" },
];

/* =========================
   Page
   ========================= */
export default function ShopNotifications() {
  const nav = useNavigate();
  const {
    items,
    loading,
    unreadCount,
    refresh,
    markOneRead,
    markAllRead,
    deleteOne,
    deleteAll,
  } = useShopNotifications();

  const [tab, setTab] = useState("all");
  const [error, setError] = useState(null);

  const handleOpen = async (n) => {
    try {
      setError(null);
      const id = n?._id || n?.id;
      const read = !!(n?.readAt || n?.read);
      if (id && !read) await markOneRead(id);
      nav(getTargetUrl(n));
    } catch {
      setError("Failed to open notification.");
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await deleteOne(id);
    } catch {
      setError("Failed to delete notification.");
    }
  };

  const handleDeleteAll = async () => {
    try {
      await deleteAll();
    } catch {
      setError("Failed to clear notifications.");
    }
  };

  // Filter
  const filtered = useMemo(() => {
    if (tab === "all") return items;
    if (tab === "unread") return items.filter((n) => !n.readAt && !n.read);
    return items.filter((n) => {
      const t = getType(n);
      if (tab === "orders") return isOrderType(t);
      if (tab === "returns") return isReturnType(t);
      if (tab === "messages") return isMessageType(t);
      if (tab === "stock") return isStockType(t);
      if (tab === "social") return isSocialType(t);
      return true;
    });
  }, [items, tab]);

  // Group by date
  const grouped = useMemo(() => {
    const groups = {};
    const now = new Date();
    const today = now.toDateString();
    const yesterday = new Date(now - 86400000).toDateString();

    for (const n of filtered) {
      const d = n.createdAt ? new Date(n.createdAt).toDateString() : "Unknown";
      let label = d;
      if (d === today) label = "Today";
      else if (d === yesterday) label = "Yesterday";
      else
        label = new Date(n.createdAt).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        });

      if (!groups[label]) groups[label] = [];
      groups[label].push(n);
    }
    return Object.entries(groups);
  }, [filtered]);

  // Unread counts per tab
  const unreadByTab = useMemo(() => {
    const counts = { all: 0, unread: 0 };
    for (const n of items) {
      if (n.readAt || n.read) continue;
      counts.all++;
      counts.unread++;
      const t = getType(n);
      if (isOrderType(t)) counts.orders = (counts.orders || 0) + 1;
      if (isReturnType(t)) counts.returns = (counts.returns || 0) + 1;
      if (isMessageType(t)) counts.messages = (counts.messages || 0) + 1;
      if (isStockType(t)) counts.stock = (counts.stock || 0) + 1;
      if (isSocialType(t)) counts.social = (counts.social || 0) + 1;
    }
    return counts;
  }, [items]);

  return (
    <div className="sn-page">
      {/* Header */}
      <div className="sn-page-head">
        <div>
          <h2 className="sn-h2">Notifications</h2>
          <div className="sn-sub">
            {unreadCount > 0 ? `${unreadCount} unread` : "All caught up ✅"}
          </div>
        </div>
        <div className="sn-actions">
          <button
            className="sn-btn"
            onClick={() => { setError(null); refresh(); }}
            type="button"
            disabled={loading}
          >
            {loading ? "…" : "↻ Refresh"}
          </button>
          <button
            className="sn-btn sn-btn-primary"
            onClick={markAllRead}
            type="button"
            disabled={unreadCount === 0}
          >
            ✓ Mark all read
          </button>
          <button
            className="sn-btn sn-btn-danger"
            onClick={handleDeleteAll}
            type="button"
            disabled={items.length === 0}
          >
            🗑 Clear all
          </button>
        </div>
      </div>

      {error && <div className="sn-error">{error}</div>}

      {/* Tabs */}
      <div className="sn-tabs">
        {TABS.map((t) => {
          const count = unreadByTab[t.key] || 0;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              className={`sn-tab ${active ? "sn-tab-active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {count > 0 && <span className="sn-tab-badge">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading && items.length === 0 ? (
        <div className="sn-empty-state">
          <div className="sn-empty-icon">⏳</div>
          <div className="sn-empty-title">Loading notifications…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="sn-empty-state">
          <div className="sn-empty-icon">
            {tab === "unread"
              ? "✅"
              : tab === "orders"
              ? "📦"
              : tab === "returns"
              ? "↩️"
              : tab === "messages"
              ? "✉️"
              : tab === "stock"
              ? "⚠️"
              : "🔔"}
          </div>
          <div className="sn-empty-title">
            {tab === "unread"
              ? "All caught up!"
              : tab === "all"
              ? "No notifications yet"
              : `No ${tab} notifications`}
          </div>
          <div className="sn-empty-sub">
            {tab === "unread"
              ? "You have no unread notifications"
              : "Notifications will appear here when something happens"}
          </div>
        </div>
      ) : (
        <div className="sn-groups">
          {grouped.map(([label, notifs]) => (
            <div key={label}>
              <div className="sn-date-label">{label}</div>
              <div className="sn-notif-list">
                {notifs.map((n) => {
                  const id = n._id || n.id;
                  const read = !!(n.readAt || n.read);
                  const t = getType(n);

                  return (
                    <div
                      key={String(id)}
                      className={`sn-notif-card ${read ? "" : "sn-notif-unread"}`}
                      onClick={() => handleOpen(n)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleOpen(n);
                        }
                      }}
                    >
                      <div className="sn-notif-icon">{typeIcon(t)}</div>
                      <div className="sn-notif-body">
                        <div className="sn-notif-text">
                          {n.title || n.type || "Notification"}
                        </div>
                        <div className="sn-notif-msg">
                          {n.actorName && !(String(n.message || "").startsWith(n.actorName)) ? <b>{n.actorName} </b> : null}
                          {n.message || n.text || n.body || ""}
                        </div>
                        <div className="sn-notif-time">{timeAgo(n.createdAt)}</div>
                      </div>
                      {!read && <div className="sn-unread-dot" />}
                      <button
                        type="button"
                        className="sn-delete-btn"
                        onClick={(e) => handleDelete(e, id)}
                        title="Delete notification"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
